import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  CreditCard, CheckCircle2, AlertTriangle, Clock, Sparkles,
  Loader2, ArrowRight, ShieldCheck, FileText, RefreshCcw, Download, Receipt,
} from "lucide-react";

const STATUS_LABELS = {
  beta:     { label: "Accès bêta — offert",    color: "#22c55e",            icon: Sparkles },
  trialing: { label: "Essai gratuit en cours", color: "var(--pit-yellow)", icon: Clock },
  active:   { label: "Abonnement actif",       color: "#22c55e",            icon: CheckCircle2 },
  expired:  { label: "Abonnement expiré",      color: "var(--board-red)",   icon: AlertTriangle },
  inactive: { label: "Aucun abonnement",       color: "var(--grey)",        icon: AlertTriangle },
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
  } catch { return "—"; }
};

export default function Subscription() {
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(false);
  const [invoices, setInvoices] = useState(null);
  const [params, setParams] = useSearchParams();

  const loadInvoices = useCallback(async () => {
    try {
      const { data } = await api.get("/billing/invoices");
      setInvoices(data);
    } catch {
      setInvoices(null);
    }
  }, []);

  useEffect(() => { loadInvoices(); }, [loadInvoices]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/billing/me");
      setSub(data);
    } catch (e) {
      toast.error("Impossible de charger votre abonnement");
    } finally {
      setLoading(false);
    }
  }, []);

  // Polling post-Stripe redirect
  useEffect(() => {
    const sessionId = params.get("session_id");
    const cancelled = params.get("cancelled");
    if (cancelled) {
      toast.info("Paiement annulé. Vous pouvez réessayer quand vous voulez.");
      params.delete("cancelled");
      setParams(params, { replace: true });
      load();
      return;
    }
    if (sessionId) {
      let attempts = 0;
      const poll = async () => {
        attempts += 1;
        try {
          const { data } = await api.get(`/billing/status/${sessionId}`);
          if (data.payment_status === "paid" || data.subscription_status === "trialing" || data.subscription_status === "active") {
            toast.success(data.subscription_status === "trialing" ? "Essai 14 jours activé. Bienvenue !" : "Paiement confirmé. Abonnement actif.");
            params.delete("session_id");
            setParams(params, { replace: true });
            await load();
            return;
          }
          if (data.status === "expired") {
            toast.error("La session de paiement a expiré. Veuillez réessayer.");
            params.delete("session_id");
            setParams(params, { replace: true });
            await load();
            return;
          }
          if (attempts < 8) {
            setTimeout(poll, 2000);
          } else {
            toast.info("Vérification en cours. Rafraîchissez la page dans quelques instants.");
            await load();
          }
        } catch (e) {
          await load();
        }
      };
      poll();
    } else {
      load();
    }
  }, [params, setParams, load]);

  const [selectedPlan, setSelectedPlan] = useState("yearly");

  const checkout = async () => {
    setCheckingOut(true);
    try {
      const { data } = await api.post("/billing/checkout", {
        origin_url: window.location.origin,
        plan: selectedPlan,
      });
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error("Impossible de créer la session de paiement");
        setCheckingOut(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de paiement");
      setCheckingOut(false);
    }
  };

  const openPortal = async () => {
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      if (data.url) window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Portail indisponible");
    }
  };

  if (loading) {
    return (
      <div className="text-tertiary-text flex items-center gap-2"><span className="spinner" /> Chargement…</div>
    );
  }
  if (!sub) {
    return (
      <div className="card p-8 max-w-lg mx-auto text-center" data-testid="sub-load-error">
        <AlertTriangle size={28} style={{ color: "var(--pit-yellow)" }} className="mx-auto mb-3" />
        <div className="font-display text-xl mb-2" style={{ fontWeight: 700 }}>Impossible de charger votre abonnement</div>
        <div className="text-sm mb-5" style={{ color: "var(--grey)" }}>Une erreur temporaire s'est produite. Réessayez dans un instant.</div>
        <button onClick={() => { setLoading(true); load(); loadInvoices(); }} className="btn btn-primary" data-testid="sub-retry-btn">
          <RefreshCcw size={14} /> Réessayer
        </button>
      </div>
    );
  }

  const statusKey = sub.status in STATUS_LABELS ? sub.status : "inactive";
  const statusMeta = STATUS_LABELS[statusKey];
  const StatusIcon = statusMeta.icon;
  const isExpiring = sub.is_active && sub.days_remaining <= 7 && !sub.beta_free;
  const isExpired = !sub.is_active;

  return (
    <div data-testid="subscription-page" className="max-w-5xl mx-auto">
      <header className="mb-8 animate-in">
        <div className="flex items-center gap-2 text-xs font-semibold mb-3" style={{ color: "#FFC400" }}>
          <CreditCard size={13} strokeWidth={2} /> Abonnement PitBoard Pro
        </div>
        <h1 className="font-display text-[2.5rem] sm:text-5xl tracking-tight leading-[1.05]" style={{ fontWeight: 500 }}>
          Votre <span style={{ color: "#FFC400" }}>abonnement</span>
        </h1>
        <p className="text-secondary-text mt-3 max-w-3xl text-[15px]">
          Gérez votre abonnement, vos paiements et téléchargez vos factures. Aucune carte bancaire n'est stockée sur PitBoard : tout est sécurisé via Stripe.
        </p>
      </header>

      {/* Status card */}
      <div className="card p-6 sm:p-8 mb-6" data-testid="sub-status-card">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold mb-3"
                 style={{ background: `${statusMeta.color}1A`, color: statusMeta.color }}>
              <StatusIcon size={13} /> {statusMeta.label}
            </div>
            <div className="font-display text-3xl" style={{ fontWeight: 700 }}>
              PitBoard Pro · 19 € HT<span className="text-base font-normal" style={{ color: "var(--ink-soft)" }}>/mois</span>
            </div>
            <div className="text-sm mt-1" style={{ color: "var(--grey)" }}>
              Soit 22,80 € TTC/mois (TVA 20 %) · Le plus complet et le moins cher de France
            </div>
          </div>

          <div className="text-right">
            <div className="text-xs" style={{ color: "var(--grey)" }}>
              {sub.beta_free ? "Statut" : sub.status === "trialing" ? "Essai termine le" : sub.status === "active" ? "Prochain renouvellement" : "Expiré depuis"}
            </div>
            <div className="font-display text-xl mt-0.5" style={{ fontWeight: 700, color: statusMeta.color }}>
              {sub.beta_free ? "Gratuit pendant la bêta" : fmtDate(sub.ends_at)}
            </div>
            {sub.is_active && !sub.beta_free && (
              <div className="text-sm mt-1 numerals" data-testid="sub-days-remaining">
                {sub.days_remaining} jour{sub.days_remaining > 1 ? "s" : ""} restant{sub.days_remaining > 1 ? "s" : ""}
              </div>
            )}
          </div>
        </div>

        {/* Alerts */}
        {isExpired && (
          <div className="rounded-lg p-4 mb-5 flex items-start gap-3" style={{ background: "rgba(225,6,0,0.10)", border: "1px solid rgba(225,6,0,0.35)" }}>
            <AlertTriangle size={18} style={{ color: "#E10600" }} className="shrink-0 mt-0.5" />
            <div className="text-sm">
              <div className="font-semibold mb-0.5" style={{ color: "#E10600" }}>Accès restreint</div>
              <div style={{ color: "var(--chalk)" }}>Votre période est terminée. Renouvelez pour retrouver l'accès complet à PitBoard.</div>
            </div>
          </div>
        )}
        {isExpiring && !isExpired && (
          <div className="rounded-lg p-4 mb-5 flex items-start gap-3" style={{ background: "rgba(255,196,0,0.10)", border: "1px solid rgba(255,196,0,0.35)" }}>
            <Clock size={18} style={{ color: "var(--pit-yellow)" }} className="shrink-0 mt-0.5" />
            <div className="text-sm">
              <div className="font-semibold mb-0.5" style={{ color: "var(--pit-yellow)" }}>Bientôt à renouveler</div>
              <div style={{ color: "var(--chalk)" }}>
                Plus que {sub.days_remaining} jour{sub.days_remaining > 1 ? "s" : ""}. Renouvelez dès maintenant pour ne pas perdre l'accès.
              </div>
            </div>
          </div>
        )}

        {/* CTA */}
        {sub.beta_free ? (
          <div className="rounded-lg p-4 flex items-start gap-3" data-testid="beta-free-banner"
               style={{ background: "rgba(34,197,94,0.10)", border: "1px solid rgba(34,197,94,0.35)" }}>
            <Sparkles size={18} style={{ color: "#22c55e" }} className="shrink-0 mt-0.5" />
            <div className="text-sm">
              <div className="font-semibold mb-0.5" style={{ color: "#22c55e" }}>Bêta-testeur — accès 100 % gratuit</div>
              <div style={{ color: "var(--chalk)" }}>
                Merci de nous aider à tester PitBoard ! Votre accès est offert pendant toute la durée de la bêta,
                aucun paiement ne vous sera demandé. Vous serez prévenu à l'avance avant l'ouverture officielle.
              </div>
            </div>
          </div>
        ) : (
        <div className="flex flex-wrap gap-3">
          {!sub.has_stripe_customer && (
            <>
              {/* Plan selector — visible uniquement avant la 1ère souscription */}
              <div className="w-full mb-2 grid sm:grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setSelectedPlan("monthly")}
                  className={`text-left rounded-lg p-4 border-2 transition-colors ${selectedPlan === "monthly" ? "border-[#FFC400]" : "border-[var(--border)]"}`}
                  data-testid="plan-monthly"
                >
                  <div className="text-xs font-semibold mb-1" style={{ color: "var(--ink-soft)" }}>MENSUEL</div>
                  <div className="font-display text-2xl numerals" style={{ fontWeight: 700 }}>
                    {sub.plans?.monthly?.price_ht} € <span className="text-sm font-normal" style={{ color: "var(--grey)" }}>HT/mois</span>
                  </div>
                  <div className="text-xs mt-1" style={{ color: "var(--grey)" }}>
                    Soit {sub.plans?.monthly?.price_ttc} € TTC · Prélevé chaque mois · résiliation libre
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedPlan("yearly")}
                  className={`text-left rounded-lg p-4 border-2 transition-colors relative ${selectedPlan === "yearly" ? "border-[#FFC400]" : "border-[var(--border)]"}`}
                  data-testid="plan-yearly"
                >
                  {sub.plans?.yearly?.savings_pct > 0 && (
                    <span className="absolute -top-2 right-3 text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: "#FFC400", color: "#0B0B0C" }}>
                      ÉCONOMIE {sub.plans.yearly.savings_pct}%
                    </span>
                  )}
                  <div className="text-xs font-semibold mb-1" style={{ color: "var(--ink-soft)" }}>ANNUEL</div>
                  <div className="font-display text-2xl numerals" style={{ fontWeight: 700 }}>
                    {sub.plans?.yearly?.price_ht} € <span className="text-sm font-normal" style={{ color: "var(--grey)" }}>HT/an</span>
                  </div>
                  <div className="text-xs mt-1" style={{ color: "var(--grey)" }}>
                    Soit {sub.plans?.yearly?.price_ttc} € TTC · {sub.plans?.yearly?.monthly_equivalent_ht} €/mois HT · ~2 mois offerts
                  </div>
                </button>
              </div>
            </>
          )}
          <button
            onClick={checkout}
            disabled={checkingOut}
            className="btn btn-primary"
            style={{ minHeight: 50 }}
            data-testid="sub-checkout-btn"
          >
            {checkingOut ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />}
            {sub.has_stripe_customer
              ? "Réactiver / changer de plan"
              : selectedPlan === "yearly"
                ? `Démarrer l'essai 14 jours — puis 199 € HT/an (238,80 € TTC)`
                : `Démarrer l'essai 14 jours — puis 19 € HT/mois (22,80 € TTC)`}
            <ArrowRight size={14} />
          </button>
          {sub.has_stripe_customer && (
            <button onClick={openPortal} className="btn btn-outline" data-testid="sub-portal-btn">
              <CreditCard size={14} /> Gérer mon abonnement
            </button>
          )}
          <button onClick={load} className="btn btn-outline" data-testid="sub-refresh-btn">
            <RefreshCcw size={14} /> Actualiser
          </button>
        </div>
        )}
      </div>

      {/* Factures & reçus */}
      {invoices && (
        <div className="card p-6 sm:p-8 mb-6" data-testid="billing-invoices-card">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
            <div>
              <div className="font-display text-2xl" style={{ fontWeight: 700 }}>Vos factures & reçus</div>
              <div className="text-sm mt-1" style={{ color: "var(--grey)" }}>
                Abonnement mensuel/annuel et achats de packs métiers. Téléchargement direct.
              </div>
            </div>
            {invoices.has_stripe_customer && (
              <button onClick={openPortal} className="btn btn-outline" data-testid="update-payment-method-btn">
                <CreditCard size={14} /> Modifier mon moyen de paiement
              </button>
            )}
          </div>
          {invoices.items.length === 0 ? (
            <div className="text-sm" style={{ color: "var(--grey)" }}>
              Aucune facture pour le moment.
              {!invoices.has_stripe_customer && " Vos factures et le bouton de modification du moyen de paiement apparaîtront ici après votre premier paiement."}
            </div>
          ) : (
            <div className="divide-y" style={{ borderColor: "var(--border)" }}>
              {invoices.items.map((it, i) => (
                <div key={i} className="flex items-center justify-between gap-3 py-3 flex-wrap" data-testid={`invoice-row-${i}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    {it.type === "abonnement"
                      ? <FileText size={16} style={{ color: "var(--pit-yellow)" }} className="shrink-0" />
                      : <Receipt size={16} style={{ color: "#22c55e" }} className="shrink-0" />}
                    <div className="min-w-0">
                      <div className="text-sm font-semibold truncate">{it.label}{it.number ? ` · ${it.number}` : ""}</div>
                      <div className="text-xs" style={{ color: "var(--grey)" }}>
                        {it.date ? new Date(it.date * 1000).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }) : "—"}
                        {it.status === "paid" ? " · Payée" : it.status === "open" ? " · En attente" : ""}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="font-display numerals" style={{ fontWeight: 700 }}>{it.amount_ttc.toFixed(2).replace(".", ",")} €</span>
                    {(it.pdf_url || it.hosted_url) && (
                      <a href={it.pdf_url || it.hosted_url} target="_blank" rel="noreferrer"
                         className="btn btn-outline" style={{ padding: "6px 12px", fontSize: 13 }}
                         data-testid={`invoice-download-${i}`}>
                        <Download size={13} /> {it.pdf_url ? "PDF" : "Reçu"}
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* What's included */}
      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <InfoCard icon={Sparkles} title="Tout inclus" desc="Devis, factures, stock, trésorerie, CRM, fiches chantier, PDFs personnalisés." />
        <InfoCard icon={ShieldCheck} title="Sécurité bancaire" desc="Paiement traité par Stripe (PCI-DSS niveau 1). Aucune donnée carte chez nous." />
        <InfoCard icon={FileText} title="Facture TVA française" desc="TVA 20 % collectée par MJSA INVEST. Factures téléchargeables après chaque paiement." />
      </div>

      <p className="text-xs text-center mt-8" style={{ color: "var(--grey)" }}>
        En vous abonnant, vous acceptez les <Link to="/cgu" className="underline">CGU</Link> et la <Link to="/confidentialite" className="underline">politique de confidentialité</Link>.
        Annulation sans frais en arrêtant simplement le renouvellement.
      </p>
    </div>
  );
}

function InfoCard({ icon: Icon, title, desc }) {
  return (
    <div className="card p-5">
      <Icon size={18} style={{ color: "var(--pit-yellow)" }} className="mb-3" strokeWidth={2} />
      <div className="font-semibold mb-1">{title}</div>
      <div className="text-sm" style={{ color: "var(--ink-soft)" }}>{desc}</div>
    </div>
  );
}
